import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Drawer } from "@/components/ui/Modal.jsx";
import Button from "@/components/ui/Button.jsx";
import { Field, Input, PasswordInput, Select, SwitchRow } from "@/components/ui/Form.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import { ROLES } from "@/lib/tokens.js";

/*
 * Add / edit user. Ported from the modal in users.html.
 *
 * The fields here are exactly the ones the API accepts, and no others:
 *
 *   create  POST  /api/users        { email, name, password, role }
 *   edit    PATCH /api/users/:id    { name, role, isActive }
 *
 * Email is create-only because updateUser does not read it — offering an
 * editable email field would look like it worked and change nothing.
 *
 * NO TEAM FIELD, deliberately. The current app has one, and it does nothing:
 * createUser/updateUser both drop `team` on the floor. Team membership is
 * written by POST /api/teams/:id/members, which also maintains the team's own
 * members array — the two records must move together, so the Teams page owns
 * that flow rather than this form half-doing it.
 */

const base = {
    name: z.string().trim().min(1, "Please enter the user's name"),
    role: z.enum(["admin", "manager", "employee"]),
    isActive: z.boolean(),
};

const createSchema = z.object({
    ...base,
    email: z
        .string()
        .trim()
        .min(1, "Please enter an email address")
        .email("Please enter a valid email address"),
    password: z.string().min(8, "Password must be at least 8 characters long"),
});

const editSchema = z.object(base);

export default function UserFormDrawer({ open, user, onClose, onSubmit, saving, error }) {
    const editing = Boolean(user);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(editing ? editSchema : createSchema),
        mode: "onSubmit",
        reValidateMode: "onChange",
    });

    // Reset on every open so a cancelled edit does not leak into the next one.
    useEffect(() => {
        if (!open) return;
        reset(
            editing
                ? { name: user.name, role: user.role, isActive: user.isActive }
                : { name: "", email: "", password: "", role: "employee", isActive: true },
        );
    }, [open, user, editing, reset]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={editing ? "Edit user" : "Add user"}
            subtitle={editing ? user.email : "Creates an account in your organization"}
            footer={
                <>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        form="user-form"
                        loading={saving}
                        data-save-user
                    >
                        {editing ? "Save changes" : "Create user"}
                    </Button>
                </>
            }
        >
            <form id="user-form" onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
                {error && <Callout tone="danger">{error}</Callout>}

                <Field label="Full name" htmlFor="u-name" error={errors.name?.message}>
                    <Input id="u-name" data-autofocus autoComplete="off" {...register("name")} />
                </Field>

                {editing ? (
                    <Field label="Email" htmlFor="u-email" hint="The email is the sign-in username and cannot be changed.">
                        <Input id="u-email" value={user.email} disabled readOnly />
                    </Field>
                ) : (
                    <>
                        <Field label="Email" htmlFor="u-email" error={errors.email?.message}>
                            <Input
                                id="u-email"
                                type="email"
                                autoComplete="off"
                                placeholder="name@company.com"
                                {...register("email")}
                            />
                        </Field>

                        <Field
                            label="Password"
                            htmlFor="u-password"
                            error={errors.password?.message}
                            hint="At least 8 characters. Share it with them securely; they can change it from their profile."
                        >
                            <PasswordInput
                                id="u-password"
                                autoComplete="new-password"
                                {...register("password")}
                            />
                        </Field>
                    </>
                )}

                <Field label="Role" htmlFor="u-role" error={errors.role?.message}>
                    <Select id="u-role" {...register("role")}>
                        {["employee", "manager", "admin"].map((role) => (
                            <option key={role} value={role}>
                                {ROLES[role].label}
                            </option>
                        ))}
                    </Select>
                </Field>

                <SwitchRow
                    label="Active"
                    hint="An inactive user cannot sign in or track. The desktop app signs them out on its next settings sync."
                    {...register("isActive")}
                />

                <Callout tone="info">
                    Team membership is managed on the <strong className="font-semibold">Teams</strong>{" "}
                    page — assigning a team also updates that team's member list, so both stay in
                    step.
                </Callout>
            </form>
        </Drawer>
    );
}
